import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, X, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { LanguageToggle } from "@/pages/LandingPageEN";

/**
 * Landing page Revela — rev. 2026-05-28.
 *
 * Design: silver + white palette · accenti in oro · Cormorant Garamond per i display headings.
 * Tone: maturo, raffinato, scientifico ma caldo.
 * CTA ripetuto: "Inizia il tuo primo check-in" → /login.
 */
const LandingPage = () => {
  const navigate = useNavigate();
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [requestType, setRequestType] = useState(null); // "beta" | "partner" | null

  // Auto-redirect logged-in users straight to /oggi so they don't re-see marketing
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/auth/me");
        if (!cancelled && res?.data?.user_id) {
          navigate("/oggi", { replace: true });
          return;
        }
      } catch {
        // Not authenticated → stay on landing
      }
      if (!cancelled) setCheckingAuth(false);
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const openBetaForm = () => setRequestType("beta");
  const openPartnerForm = () => setRequestType("partner");
  const closeForm = () => setRequestType(null);

  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" strokeWidth={1.5} />
      </div>
    );
  }

  const frameworks = [
    "Russell — Circumplex dell'affetto",
    "Kahneman — Day Reconstruction Method",
    "Csíkszentmihályi — Flow & Experience Sampling",
    "Karasek — Demand / Control",
    "Deci & Ryan — Self-Determination Theory",
    "Sonnentag — Recovery Experiences",
    "Bandura — Self-efficacy",
    "Bowlby — Attachment & legami significativi",
    "Seligman — Learned Helplessness",
    "Cacioppo — Solitudine percepita",
    "Lewinsohn — Behavioral Activation",
    "Trull & Ebner-Priemer — Ecological Momentary Assessment",
  ];

  const clusters = [
    {
      cluster: "Cluster benessere generale",
      items: [
        { title: "Indice di benessere giornaliero", desc: "Con trend a 7 e 30 giorni." },
        { title: "Riassunto settimanale", desc: "Una sintesi narrativa scritta in linguaggio naturale, settimana dopo settimana." },
        { title: "Velocità di recupero emotivo", desc: "Quanto tempo impieghi a tornare in equilibrio dopo un episodio negativo." },
      ],
    },
    {
      cluster: "Cluster lavoro e attività",
      items: [
        { title: "Come stai vivendo lavoro e impegni", desc: "Identifichiamo se sei sotto pressione, in sfida attiva, a tuo agio o in stallo, in ogni area della tua vita." },
        { title: "Equilibrio carico-recupero", desc: "Quanto le tue pause compensano davvero il tuo sforzo." },
        { title: "Paradosso del controllo", desc: "Quando sentirti in controllo non basta a star bene." },
      ],
    },
    {
      cluster: "Cluster motivazionale",
      subtitle: "Self-Determination Theory",
      items: [
        { title: "Indice di autonomia", desc: "Quanto le tue azioni le scegli davvero." },
        { title: "Indice di competenza", desc: "Quanto ti senti capace nelle situazioni che pesano." },
        { title: "Indice di connessione", desc: "Nutrimento netto dai legami stretti." },
      ],
    },
    {
      cluster: "Cluster affetto e regolazione",
      items: [
        { title: "Mappa affettiva", desc: "Flow · Serenità · Agitazione · Apatia." },
        { title: "Oscillazioni emotive nella giornata", desc: "Quanto oscilla il tuo umore nell'arco della giornata." },
        { title: "Persistenza emotiva", desc: "Quanto un'emozione ti resta addosso da un momento all'altro." },
        { title: "Resilienza affettiva", desc: "La tua capacità di resistere sotto carico elevato." },
      ],
    },
    {
      cluster: "Cluster relazionale",
      items: [
        { title: "Asimmetria nelle relazioni", desc: "Quali legami nutrono, quali drenano." },
        { title: "Solitudine nutriente vs solitudine pesante", desc: "Stesso «stare soli», segno opposto." },
        { title: "Senso di impotenza nei contesti", desc: "Dove hai smesso di credere di poter incidere." },
      ],
    },
  ];

  const normativi = {
    cluster: "Confronti normativi",
    items: [
      { title: "Confronto con te nel tempo", desc: "Tu sei il tuo primo riferimento." },
      { title: "Confronto con la popolazione italiana", desc: "Norme ISTAT BES." },
      { title: "Confronto con la comunità Revela", desc: "Sempre anonimo e aggregato." },
    ],
  };

  const steps = [
    {
      n: "1",
      title: "Fai un check-in in meno di 25 secondi",
      body: "Quando ti va, oppure quando l'app ti manda un piccolo ping. Quattro brevi domande: dove sei, con chi, cosa fai, come ti senti.",
    },
    {
      n: "2",
      title: "Lascia che i pattern emergano",
      body: "Le prime scoperte si sbloccano dopo pochi check-in. A 4 settimane vedi mappe affidabili. A 3 mesi vedi te stesso con una chiarezza nuova.",
    },
    {
      n: "3",
      title: "Sperimenta i suggerimenti, se ti risuonano",
      body: "Mano a mano che Revela impara a conoscerti, ti propone piccoli spunti pensati su misura. Sta a te decidere se metterli in pratica, e in che modo.",
    },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-800" data-testid="landing-page-revela">
      <LanguageToggle current="it" />

      {/* ===== HERO ===== */}
      <section className="border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 pt-16 sm:pt-24 pb-16 sm:pb-20">
          <div className="space-y-6">
            <div>
              <p className="font-display text-xl sm:text-2xl text-slate-500 italic font-light tracking-wide" data-testid="hero-hello">
                Hello
              </p>
              <h1 className="font-display text-6xl sm:text-7xl md:text-8xl text-gold leading-none mt-1" data-testid="hero-revela">
                Revela
              </h1>
            </div>

            <p className="font-display text-2xl sm:text-3xl md:text-4xl text-slate-600 italic font-light leading-snug max-w-2xl">
              Traccia le tue giornate. Scopri i tuoi pattern.
            </p>

            <ul className="text-lg sm:text-xl text-slate-600 leading-relaxed font-light max-w-2xl space-y-2" data-testid="hero-promise">
              <li>Registra le tue esperienze in <strong className="font-medium">meno di 25 secondi</strong>.</li>
              <li className="text-gold font-display italic text-2xl sm:text-3xl leading-snug pt-2">
                Scopri progressivamente il tuo funzionamento.
              </li>
              <li className="text-gold font-display italic text-2xl sm:text-3xl leading-snug">
                Ricevi suggerimenti pensati su misura per te.
              </li>
              <li className="text-gold font-display italic text-2xl sm:text-3xl leading-snug">
                Migliora nel tempo la qualità delle tue giornate e il tuo benessere.
              </li>
              <li className="text-slate-500 italic pt-2">Più dati raccogli, più i pattern emergono — e più precisi diventano.</li>
            </ul>

            <div className="pt-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-start">
              <button
                onClick={openBetaForm}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white text-base font-light tracking-wide hover:shadow-lg transition-shadow"
                data-testid="hero-cta-beta-tester"
              >
                Richiedi di diventare beta tester
              </button>
              <button
                onClick={openPartnerForm}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-amber-700/40 text-slate-700 text-base font-light tracking-wide hover:bg-amber-50/50 hover:border-amber-700/60 transition-colors"
                data-testid="hero-cta-partner"
              >
                Partecipa come partner scientifico
              </button>
            </div>

            <p className="text-sm sm:text-base text-slate-600 font-light tracking-wide" data-testid="hero-already-registered">
              Sei già iscritto?{" "}
              <Link to="/login" className="text-slate-800 underline decoration-amber-700/40 underline-offset-2 hover:decoration-amber-700/80 transition-colors font-medium" data-testid="hero-already-registered-link">
                Accedi
              </Link>
            </p>

            <p className="text-sm text-slate-500 font-light tracking-wide pt-3 max-w-2xl" data-testid="hero-beta-note">
              🌱 Revela è in <strong className="font-medium text-slate-700">fase beta</strong> e per ora è completamente gratuita. Come beta tester accedi subito all'app, inserisci i tuoi check-in e in pochi giorni cominci a vedere i tuoi primi pattern di funzionamento. Sviluppata da <strong className="font-medium text-slate-700">Laura Piccinini</strong>, psicologa e psicoterapeuta ·{" "}
              <a
                href="https://www.laurapiccinini.it"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-600 hover:text-slate-800 underline decoration-slate-300 underline-offset-2"
              >
                www.laurapiccinini.it
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* ===== COSA SCOPRIRAI — INDICI DELLE VARIABILI ===== */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-16 sm:py-20">
          <p className="font-display text-xl sm:text-2xl md:text-3xl text-slate-600 italic font-light mb-4 text-center">
            Cosa puoi scoprire dopo qualche settimana
          </p>
          <p className="text-base sm:text-lg text-slate-600 text-center max-w-2xl mx-auto mb-12 font-light leading-relaxed">
            Oltre <strong className="font-medium">70 indicatori di benessere</strong> e <strong className="font-medium">3.000 configurazioni esperienziali</strong> distinte, organizzati in <em>cinque famiglie di scoperte</em> che si sbloccano progressivamente.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="indicators-grid">
            {clusters.map((group, i) => {
              const isOrphan = i === clusters.length - 1 && clusters.length % 2 === 1;
              return (
                <div
                  key={i}
                  className={`bg-white rounded-lg p-6 sm:p-7 box-gold box-gold-accent hover:shadow-md transition-shadow ${isOrphan ? "md:col-span-2 md:max-w-[calc(50%-0.625rem)] md:mx-auto md:w-full" : ""}`}
                  data-testid={`cluster-${i}`}
                >
                  <p className="text-xs sm:text-sm text-gold font-medium tracking-[0.18em] uppercase mb-1">
                    {group.cluster}
                  </p>
                  {group.subtitle && (
                    <p className="font-display text-base text-slate-500 italic font-light mb-4">
                      {group.subtitle}
                    </p>
                  )}
                  <ul className={`space-y-5 ${group.subtitle ? "" : "mt-4"}`}>
                    {group.items.map((it, j) => (
                      <li key={j} data-testid={`cluster-${i}-item-${j}`}>
                        <p className="text-sm sm:text-[15px] text-slate-800 font-semibold tracking-[0.08em] uppercase leading-snug">
                          {it.title}
                        </p>
                        <p className="text-base sm:text-[17px] text-slate-600 leading-relaxed font-light mt-1.5">
                          {it.desc}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== CONFRONTI NORMATIVI — SEZIONE A PARTE ===== */}
      <section className="border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-20">
          <p className="font-display text-xl sm:text-2xl md:text-3xl text-slate-600 italic font-light mb-4 text-center">
            E i tuoi dati, rispetto a cosa?
          </p>
          <p className="text-base sm:text-lg text-slate-600 text-center max-w-2xl mx-auto mb-10 font-light leading-relaxed">
            Tre piani di lettura per dare senso ai tuoi numeri, senza mai uscire dal rispetto della tua privacy.
          </p>

          <div className="bg-white rounded-lg p-6 sm:p-7 box-gold box-gold-accent" data-testid="cluster-normativi">
            <p className="text-xs sm:text-sm text-gold font-medium tracking-[0.18em] uppercase mb-4">
              {normativi.cluster}
            </p>
            <ul className="space-y-5">
              {normativi.items.map((it, j) => (
                <li key={j} data-testid={`normativi-item-${j}`}>
                  <p className="text-sm sm:text-[15px] text-slate-800 font-semibold tracking-[0.08em] uppercase leading-snug">
                    {it.title}
                  </p>
                  <p className="text-base sm:text-[17px] text-slate-600 leading-relaxed font-light mt-1.5">
                    {it.desc}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ===== PIÙ DAI, PIÙ REVELA TI RESTITUISCE ===== */}
      <section className="border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-20">
          <div className="bg-gradient-to-br from-slate-100 to-slate-50 border border-slate-200 rounded-lg p-8 sm:p-10" data-testid="more-data-block">
            <p className="font-display text-3xl sm:text-4xl md:text-5xl text-gold font-light leading-tight mb-5">
              Più dai, più Revela ti restituisce.
            </p>
            <p className="text-base sm:text-lg text-slate-700 leading-relaxed font-light mb-3">
              Più check-in inserisci, più il sistema ti conosce. Le prime scoperte arrivano già dopo qualche giorno. Dopo qualche settimana emergono i pattern. Dopo qualche mese, Revela inizia a restituirti <strong className="font-medium">letture sempre più precise</strong> del tuo funzionamento e <strong className="font-medium">suggerimenti sempre più calibrati</strong> su di te — non frasi generiche.
            </p>
            <p className="text-base text-slate-500 italic font-light">
              È un patto onesto: ti chiediamo 25 secondi alla volta, e in cambio costruiamo insieme uno specchio fedele del tuo benessere.
            </p>
          </div>
        </div>
      </section>

      {/* ===== COME FUNZIONA — BOX NUMERATI ===== */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-16 sm:py-20">
          <p className="font-display text-xl sm:text-2xl md:text-3xl text-slate-600 italic font-light mb-10 text-center">
            Come funziona
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {steps.map((step, i) => (
              <div
                key={i}
                className="bg-white rounded-lg p-6 sm:p-7 box-gold box-gold-accent hover:shadow-md transition-shadow flex flex-col"
                data-testid={`step-${i}`}
              >
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="font-display text-5xl text-gold font-light leading-none">
                    {step.n}
                  </span>
                  <span className="text-xs sm:text-sm text-gold font-medium tracking-[0.18em] uppercase">
                    Passo {step.n}
                  </span>
                </div>
                <h4 className="font-display text-2xl sm:text-[26px] text-slate-800 font-light leading-tight mb-2">
                  {step.title}
                </h4>
                <p className="text-base sm:text-[17px] text-slate-600 leading-relaxed font-light">
                  {step.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SOLO TU VEDI I TUOI DATI — PRIVACY ===== */}
      <section className="border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-20 text-center" data-testid="privacy-promise">
          <p className="text-xs sm:text-sm text-gold font-medium tracking-[0.18em] uppercase mb-4">
            Privacy
          </p>
          <p className="font-display text-3xl sm:text-4xl md:text-5xl text-gold font-light leading-tight mb-5">
            Solo tu vedi i tuoi dati.
          </p>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed font-light max-w-2xl mx-auto mb-8">
            I tuoi check-in non vengono mai mostrati a terzi. Possono essere usati solo in forma completamente anonima e aggregata, secondo lo standard k-anonimato (k=5), per migliorare l'app. Revela è progettata fin dall'origine secondo i principi del <strong className="font-medium">GDPR</strong> e della <em>privacy by design</em>.
          </p>
          <a
            href="/privacy"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-amber-700/40 text-slate-700 text-sm font-light tracking-wide hover:bg-amber-50/50 hover:border-amber-700/60 transition-colors"
            data-testid="privacy-cta"
          >
            Come trattiamo i tuoi dati
          </a>
        </div>
      </section>

      {/* ===== CHI L'HA PENSATA + FRAMEWORK ===== */}
      <section className="border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-20" data-testid="about-author">
          <p className="font-display text-xl sm:text-2xl md:text-3xl text-slate-600 italic font-light mb-6 text-center">
            Chi l'ha pensata
          </p>
          <p className="text-base sm:text-lg text-slate-700 leading-relaxed font-light mb-8">
            <strong className="font-medium">Revela è un progetto di Laura Piccinini</strong>, psicologa e psicoterapeuta.{" "}
            <a
              href="https://www.laurapiccinini.it"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-600 hover:text-slate-800 underline decoration-slate-300 underline-offset-2"
            >
              www.laurapiccinini.it
            </a>
          </p>

          <div className="bg-white rounded-lg p-6 sm:p-7 box-gold box-gold-accent" data-testid="frameworks-block">
            <p className="text-xs sm:text-sm text-gold font-medium tracking-[0.18em] uppercase mb-4">
              Framework scientifici consolidati
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {frameworks.map((fw, i) => (
                <li key={i} className="flex items-start gap-2 text-base text-slate-700 leading-relaxed font-light">
                  <span className="w-1 h-1 rounded-full bg-amber-700/40 mt-2.5 shrink-0" />
                  <span>{fw}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-slate-500 italic font-light mt-5 pt-4 border-t border-slate-100">
              Applicati con la metodologia dell'<strong className="font-medium not-italic text-slate-600">Ecological Momentary Assessment</strong> (Trull &amp; Ebner-Priemer, 2013), lo standard internazionale per studiare l'esperienza nel suo contesto reale.
            </p>
          </div>
        </div>
      </section>

      {/* ===== NOTA BETA ===== */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12 sm:py-16" data-testid="beta-note">
          <p className="font-display text-3xl sm:text-4xl text-slate-700 font-light leading-tight mb-4">
            Una versione di prova, fatta con cura.
          </p>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed font-light">
            Revela è in <strong className="font-medium">fase beta</strong>: stiamo affinando le interpretazioni, le soglie e le letture personalizzate insieme alle prime persone che la usano. Significa che potresti trovare piccoli dettagli da limare — ma anche che la tua esperienza contribuisce davvero a costruirla. Per ora è completamente gratuita.
          </p>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section>
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-20 sm:py-24 text-center">
          <p className="font-display text-3xl sm:text-4xl md:text-5xl text-slate-800 font-light leading-tight mb-8">
            Pronto a scoprire i tuoi pattern?
          </p>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-center">
            <button
              onClick={openBetaForm}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white text-base font-light tracking-wide hover:shadow-lg transition-shadow"
              data-testid="final-cta-beta-tester"
            >
              Richiedi di diventare beta tester
            </button>
            <button
              onClick={openPartnerForm}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-amber-700/40 text-slate-700 text-base font-light tracking-wide hover:bg-amber-50/50 hover:border-amber-700/60 transition-colors"
              data-testid="final-cta-partner"
            >
              Partecipa come partner scientifico
            </button>
          </div>
        </div>
      </section>

      {/* ===== DISCLAIMER NON-CLINICO ===== */}
      <section className="bg-white border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-8 text-center" data-testid="non-clinical-disclaimer">
          <p className="text-xs sm:text-sm text-slate-500 italic font-light leading-relaxed">
            Revela non fornisce valutazioni cliniche e non sostituisce in alcun modo una consulenza professionale. È uno strumento che ti aiuta a osservare pattern soggettivi nelle tue esperienze quotidiane. I suggerimenti che riceverai sono spunti di riflessione, da valutare sempre con il tuo personale discernimento.
          </p>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-6 text-center">
          <p className="text-[11px] text-slate-400 font-light tracking-wide">
            © {new Date().getFullYear()} Revela · Laura Piccinini ·{" "}
            <a href="/privacy" className="hover:text-slate-600 underline decoration-slate-200 underline-offset-2">Privacy</a>
            {" · "}
            <a href="/termini" className="hover:text-slate-600 underline decoration-slate-200 underline-offset-2">Termini</a>
          </p>
        </div>
      </footer>

      {/* ===== REQUEST FORM MODAL ===== */}
      {requestType && (
        <RequestModal type={requestType} onClose={closeForm} />
      )}
    </div>
  );
};

/**
 * RequestModal — small inline modal for beta tester / partner scientifico requests.
 * Sends POST /api/landing/contact-request which persists to MongoDB.
 */
const RequestModal = ({ type, onClose }) => {
  const isBeta = type === "beta";
  const title = isBeta ? "Richiedi di diventare beta tester" : "Partecipa come partner scientifico";
  const intro = isBeta
    ? "Lasciaci i tuoi contatti: ti scriveremo per attivarti come beta tester. Avrai accesso subito all'app, potrai inserire i tuoi check-in e in pochi giorni cominciare a vedere i tuoi primi pattern di funzionamento."
    : "Lasciaci i tuoi contatti e una breve nota: ti contatteremo per esplorare insieme una collaborazione scientifica (ricerca, validazione, partnership accademica).";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!name.trim() || !email.trim()) {
      setError("Nome ed email sono obbligatori.");
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(email.trim())) {
      setError("L'indirizzo email non sembra valido.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/landing/contact-request", {
        type,
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      setError("Qualcosa non ha funzionato. Riprova fra un momento.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm px-4 py-8"
      onClick={onClose}
      data-testid="request-modal-backdrop"
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid={`request-modal-${type}`}
      >
        <div className="flex items-start justify-between p-6 sm:p-8 border-b border-slate-100">
          <div>
            <p className="text-xs text-gold font-medium tracking-[0.18em] uppercase mb-1">
              {isBeta ? "Beta tester" : "Partner scientifico"}
            </p>
            <h3 className="font-display text-2xl sm:text-3xl text-slate-800 font-light leading-tight">
              {title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-100 transition-colors -mr-2 -mt-2"
            data-testid="request-modal-close"
            aria-label="Chiudi"
          >
            <X className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
          </button>
        </div>

        {submitted ? (
          <div className="p-8 text-center" data-testid="request-modal-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" strokeWidth={1.5} />
            <p className="font-display text-xl text-slate-700 mb-3">Richiesta ricevuta. Grazie.</p>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-6">
              Ti risponderemo personalmente all'indirizzo {email} nei prossimi giorni.
            </p>
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white text-sm font-light tracking-wide hover:shadow-lg transition-shadow"
              data-testid="request-modal-close-success"
            >
              Chiudi
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="p-6 sm:p-8 space-y-5">
            <p className="text-base text-slate-600 leading-relaxed font-light">
              {intro}
            </p>

            <div>
              <label className="block text-xs text-slate-500 font-medium tracking-[0.12em] uppercase mb-2">
                Nome e cognome *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg text-slate-800 font-light focus:outline-none focus:border-amber-700/60 focus:ring-1 focus:ring-amber-700/30 transition-colors"
                placeholder="Es. Maria Rossi"
                data-testid="request-modal-name"
                disabled={submitting}
              />
            </div>

            <div>
              <label className="block text-xs text-slate-500 font-medium tracking-[0.12em] uppercase mb-2">
                Email *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg text-slate-800 font-light focus:outline-none focus:border-amber-700/60 focus:ring-1 focus:ring-amber-700/30 transition-colors"
                placeholder="la-tua-email@esempio.it"
                data-testid="request-modal-email"
                disabled={submitting}
              />
            </div>

            <div>
              <label className="block text-xs text-slate-500 font-medium tracking-[0.12em] uppercase mb-2">
                {isBeta ? "Vuoi raccontarci qualcosa? (facoltativo)" : "In che modo vorresti collaborare? (facoltativo)"}
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg text-slate-800 font-light focus:outline-none focus:border-amber-700/60 focus:ring-1 focus:ring-amber-700/30 transition-colors resize-none"
                placeholder={isBeta
                  ? "Es. Mi occupo di benessere e mi interessano i pattern psicologici nelle esperienze quotidiane..."
                  : "Es. Sono ricercatrice all'Università di..., lavoro su EMA e regolazione affettiva..."}
                data-testid="request-modal-message"
                disabled={submitting}
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 font-light" data-testid="request-modal-error">
                {error}
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white text-sm font-light tracking-wide hover:shadow-lg transition-shadow disabled:opacity-60 disabled:cursor-not-allowed flex-1"
                data-testid="request-modal-submit"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Invio in corso...</>
                ) : (
                  "Invia richiesta"
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-6 py-3 rounded-full border border-slate-200 text-slate-600 text-sm font-light tracking-wide hover:bg-slate-50 transition-colors disabled:opacity-60"
                data-testid="request-modal-cancel"
              >
                Annulla
              </button>
            </div>

            <p className="text-xs text-slate-400 italic font-light pt-2">
              I tuoi dati saranno usati solo per ricontattarti, nel rispetto del GDPR.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default LandingPage;
